import React, { useState, useEffect } from 'react';
import { collection, getDocs, orderBy, query, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { useNotification } from '../contexts/NotificationContext';
import LoadingSpinner from './shared/LoadingSpinner';

const FEEDBACK_TYPES = {
  'bug': { label: 'Bug Report', icon: '🐛', color: 'bg-red-50 text-red-700 border border-red-200' },
  'feature': { label: 'Feature Request', icon: '💡', color: 'bg-amber-50 text-amber-700 border border-amber-200' },
  'general': { label: 'General', icon: '💬', color: 'bg-blue-50 text-blue-700 border border-blue-200' },
};

const ProblemReportAnalyzer = ({ db, onClose }) => {
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filter, setFilter] = useState('all');
  const [updatingFlag, setUpdatingFlag] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Available flags for feedback reports
  const FLAGS = {
    'test': { label: 'Test', color: 'bg-purple-100 text-purple-800', icon: '🧪' },
    'action-needed': { label: 'Action Needed', color: 'bg-red-100 text-red-800', icon: '⚠️' },
    'no-fix-needed': { label: 'No Fix Needed', color: 'bg-gray-100 text-gray-800', icon: '✋' },
    'fixed': { label: 'Fixed', color: 'bg-green-100 text-green-800', icon: '✅' },
    'investigating': { label: 'Investigating', color: 'bg-yellow-100 text-yellow-800', icon: '🔍' },
    'duplicate': { label: 'Duplicate', color: 'bg-blue-100 text-blue-800', icon: '📋' }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const q = query(
        collection(db, 'problem-reports'),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReports(reportsData);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateReportFlag = async (reportId, flag) => {
    try {
      setUpdatingFlag(true);
      const reportRef = doc(db, 'problem-reports', reportId);
      await updateDoc(reportRef, {
        flag: flag,
        flagUpdatedAt: new Date(),
        flagUpdatedBy: 'admin' // Could be enhanced to track specific admin
      });
            
      // Update local state
      setReports(prevReports => 
        prevReports.map(report => 
          report.id === reportId 
            ? { ...report, flag, flagUpdatedAt: new Date() }
            : report
        )
      );
            
      // Update selected report if it's the one being updated
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => ({ ...prev, flag, flagUpdatedAt: new Date() }));
      }
            
      console.log(`Report ${reportId} flagged as: ${flag}`);
      showSuccess('Flag updated successfully');
    } catch (error) {
      console.error('Failed to update report flag:', error);
      showError('Failed to update flag. Please try again.');
    } finally {
      setUpdatingFlag(false);
    }
  };

  const toggleSelect = (reportId, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(reportId)) {
        next.delete(reportId);
      } else {
        next.add(reportId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredReports.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredReports.map(r => r.id)));
    }
  };

  const bulkUpdateFlag = async (flag) => {
    if (selectedIds.size === 0) return;

    try {
      setBulkUpdating(true);
      const batch = writeBatch(db);
      const now = new Date();

      selectedIds.forEach(id => {
        const reportRef = doc(db, 'problem-reports', id);
        batch.update(reportRef, {
          flag: flag || null,
          flagUpdatedAt: now,
          flagUpdatedBy: 'admin'
        });
      });

      await batch.commit();

      // Update local state
      setReports(prev =>
        prev.map(report =>
          selectedIds.has(report.id)
            ? { ...report, flag: flag || null, flagUpdatedAt: now }
            : report
        )
      );

      // Update selected report if it was in the batch
      if (selectedReport && selectedIds.has(selectedReport.id)) {
        setSelectedReport(prev => ({ ...prev, flag: flag || null, flagUpdatedAt: now }));
      }

      showSuccess(`Updated ${selectedIds.size} ${selectedIds.size === 1 ? 'entry' : 'entries'}`);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Bulk update failed:', error);
      showError('Bulk update failed. Please try again.');
    } finally {
      setBulkUpdating(false);
    }
  };

  const getErrorSummary = (report) => {
    const errors = [];
        
    // Check console logs for errors
    const errorLogs = report.consoleLogs?.filter(log => log.level === 'error') || [];
    if (errorLogs.length > 0) {
      errors.push(`${errorLogs.length} console errors`);
    }

    // Check for common issues
    if (report.debugInfo?.templatesLoading) {
      errors.push('Templates still loading');
    }
        
    if (report.debugInfo?.errorStates?.prospectDataError) {
      errors.push('Prospect data error');
    }

    if (!report.screenshot) {
      errors.push('Screenshot capture failed');
    }

    return errors.length > 0 ? errors.join(', ') : 'No obvious errors detected';
  };

  const getBugSeverity = (report) => {
    const errorLogs = report.consoleLogs?.filter(log => log.level === 'error') || [];
    if (errorLogs.length > 3) return 'high';
    if (errorLogs.length > 0) return 'medium';
    return 'low';
  };

  const filteredReports = reports.filter(report => {
    if (filter === 'high-severity') return getBugSeverity(report) === 'high';
    if (filter === 'has-errors') return report.consoleLogs?.some(log => log.level === 'error');
    if (filter === 'no-screenshot') return !report.screenshot;
    if (filter === 'unflagged') return !report.flag;
    if (filter === 'type-bug') return report.feedbackType === 'bug';
    if (filter === 'type-feature') return report.feedbackType === 'feature';
    if (filter === 'type-general') return report.feedbackType === 'general';
    if (filter === 'type-legacy') return !report.feedbackType;
    if (Object.keys(FLAGS).includes(filter)) return report.flag === filter;
    return true;
  });

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-4 rounded-lg">
          <LoadingSpinner size="medium" message="Loading feedback reports..." />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-sm w-full max-w-6xl h-5/6 flex">
        {/* Reports List */}
        <div className="w-1/2 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h2 className="text-xl font-semibold">Feedback ({reports.length})</h2>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
                        
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Feedback</option>
              <option value="unflagged">🏳️ Unflagged</option>
              <optgroup label="By Type">
                <option value="type-bug">🐛 Bug Reports</option>
                <option value="type-feature">💡 Feature Requests</option>
                <option value="type-general">💬 General</option>
                <option value="type-legacy">📦 Legacy (no type)</option>
              </optgroup>
              <optgroup label="By Flag">
                {Object.entries(FLAGS).map(([key, flag]) => (
                  <option key={key} value={key}>
                    {flag.icon} {flag.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="By Severity">
                <option value="high-severity">High Severity</option>
                <option value="has-errors">Has Console Errors</option>
                <option value="no-screenshot">Missing Screenshot</option>
              </optgroup>
            </select>
          </div>

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-3">
              <span className="text-sm font-medium text-blue-700">
                {selectedIds.size} selected
              </span>
              <select
                onChange={(e) => {
                  if (e.target.value !== '') {
                    bulkUpdateFlag(e.target.value === '__clear__' ? null : e.target.value);
                    e.target.value = '';
                  }
                }}
                disabled={bulkUpdating}
                className="text-sm border border-blue-300 rounded px-2 py-1 bg-white"
                defaultValue=""
              >
                <option value="" disabled>Set flag...</option>
                <option value="__clear__">🏳️ Clear Flag</option>
                {Object.entries(FLAGS).map(([key, flag]) => (
                  <option key={key} value={key}>
                    {flag.icon} {flag.label}
                  </option>
                ))}
              </select>
              {bulkUpdating && <LoadingSpinner size="small" inline />}
              <button
                onClick={() => setSelectedIds(new Set())}
                className="ml-auto text-xs text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </div>
          )}

          {/* Select All Row */}
          <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2 bg-gray-50">
            <input
              type="checkbox"
              checked={filteredReports.length > 0 && selectedIds.size === filteredReports.length}
              ref={(el) => {
                if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < filteredReports.length;
              }}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              aria-label="Select all"
            />
            <span className="text-xs text-gray-500">
              {filteredReports.length} {filteredReports.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
                    
          <div className="flex-1 overflow-y-auto">
            {filteredReports.map(report => (
              <div 
                key={report.id}
                onClick={() => setSelectedReport(report)}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                  selectedReport?.id === report.id ? 'bg-blue-50' : ''
                } ${selectedIds.has(report.id) ? 'bg-blue-50/50' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(report.id)}
                    onChange={(e) => toggleSelect(report.id, e)}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                    aria-label={`Select report from ${report.userEmail}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {report.userEmail}
                        </div>
                        {report.feedbackType && FEEDBACK_TYPES[report.feedbackType] && (
                          <span className={`px-1.5 py-0.5 text-xs rounded flex-shrink-0 ${FEEDBACK_TYPES[report.feedbackType].color}`}>
                            {FEEDBACK_TYPES[report.feedbackType].icon} {FEEDBACK_TYPES[report.feedbackType].label}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                        {report.flag && FLAGS[report.flag] && (
                          <div className={`px-2 py-1 text-xs rounded ${FLAGS[report.flag].color}`}>
                            {FLAGS[report.flag].icon} {FLAGS[report.flag].label}
                          </div>
                        )}
                        <div className={`px-2 py-1 text-xs rounded ${
                          getBugSeverity(report) === 'high' ? 'bg-red-100 text-red-800' :
                            getBugSeverity(report) === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                        }`}>
                          {getBugSeverity(report).toUpperCase()}
                        </div>
                      </div>
                    </div>
                                
                    <div className="text-sm text-gray-600 mb-2">
                      {report.currentPage} • {new Date(report.timestamp?.toDate?.() || report.timestamp).toLocaleString()}
                    </div>
                                
                    <div className="text-sm text-gray-800 mb-2">
                      {report.description.substring(0, 100)}{report.description.length > 100 ? '...' : ''}
                    </div>
                                
                    <div className="text-xs text-gray-500">
                      {getErrorSummary(report)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Report Details */}
        <div className="w-1/2 flex flex-col">
          {selectedReport ? (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Feedback Details</h3>
                  {selectedReport.feedbackType && FEEDBACK_TYPES[selectedReport.feedbackType] && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${FEEDBACK_TYPES[selectedReport.feedbackType].color}`}>
                      {FEEDBACK_TYPES[selectedReport.feedbackType].icon} {FEEDBACK_TYPES[selectedReport.feedbackType].label}
                    </span>
                  )}
                </div>
                                
                {/* Flag Management */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Flag:</label>
                  <select
                    value={selectedReport.flag || ''}
                    onChange={(e) => updateReportFlag(selectedReport.id, e.target.value || null)}
                    disabled={updatingFlag}
                    className="text-sm border border-gray-300 rounded px-2 py-1 min-w-32"
                  >
                    <option value="">No Flag</option>
                    {Object.entries(FLAGS).map(([key, flag]) => (
                      <option key={key} value={key}>
                        {flag.icon} {flag.label}
                      </option>
                    ))}
                  </select>
                  {updatingFlag && (
                    <LoadingSpinner size="small" inline />
                  )}
                </div>
              </div>
                            
              {/* Current Flag Display */}
              {selectedReport.flag && FLAGS[selectedReport.flag] && (
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-4 ${FLAGS[selectedReport.flag].color}`}>
                  {FLAGS[selectedReport.flag].icon} {FLAGS[selectedReport.flag].label}
                  {selectedReport.flagUpdatedAt && (
                    <span className="ml-2 text-xs opacity-75">
                                            • Updated {new Date(selectedReport.flagUpdatedAt?.toDate?.() || selectedReport.flagUpdatedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
                            
              {/* Quick Analysis */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-yellow-800 mb-2">🔍 Quick Analysis:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Severity: <strong>{getBugSeverity(selectedReport).toUpperCase()}</strong></li>
                  <li>• Page: <strong>{selectedReport.currentPage}</strong></li>
                  <li>• Console Errors: <strong>{selectedReport.consoleLogs?.filter(log => log.level === 'error').length || 0}</strong></li>
                  <li>• Screenshot: <strong>{selectedReport.screenshot ? '✅ Available' : '❌ Missing'}</strong></li>
                </ul>
              </div>

              {/* User Description */}
              <div className="mb-4">
                <h4 className="font-medium mb-2">📝 User Feedback:</h4>
                <div className="bg-gray-50 p-3 rounded border text-sm">
                  {selectedReport.description}
                </div>
              </div>

              {/* Screenshot */}
              {selectedReport.screenshot && (
                <div className="mb-4">
                  <h4 className="font-medium mb-2">📸 Screenshot:</h4>
                  <img 
                    src={selectedReport.screenshot} 
                    alt="Feedback screenshot"
                    className="max-w-full border border-gray-200 rounded cursor-pointer"
                    onClick={() => window.open(selectedReport.screenshot, '_blank')}
                  />
                </div>
              )}

              {/* Console Errors */}
              {selectedReport.consoleLogs?.filter(log => log.level === 'error').length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium mb-2">🚨 Console Errors:</h4>
                  <div className="bg-red-50 border border-red-200 rounded p-3 max-h-40 overflow-y-auto">
                    {selectedReport.consoleLogs.filter(log => log.level === 'error').map((log, i) => (
                      <div key={i} className="text-sm text-red-800 mb-2 font-mono">
                        <div className="text-xs text-red-600">{log.timestamp}</div>
                        <div>{log.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Application State */}
              <div className="mb-4">
                <h4 className="font-medium mb-2">⚙️ Application State:</h4>
                <div className="bg-gray-50 p-3 rounded border text-xs font-mono max-h-40 overflow-y-auto">
                  <pre>{JSON.stringify(selectedReport.applicationState, null, 2)}</pre>
                </div>
              </div>

              {/* Debug Actions */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium mb-2">🔧 Debug Actions:</h4>
                <div className="space-y-2">
                  <button 
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(selectedReport, null, 2))}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                  >
                                        Copy Full Report JSON
                  </button>
                  <button 
                    onClick={() => navigator.clipboard.writeText(selectedReport.consoleLogs?.map(log => `[${log.level}] ${log.message}`).join('\n') || '')}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 ml-2"
                  >
                                        Copy Console Logs
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
                            Select a feedback entry to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProblemReportAnalyzer;
